<template>
  <div class="todolist-container">
    <!-- 添加TopBar组件，并传递用户信息和应用信息 -->
    <TopBar :user="user" :apps="apps" />

    <!-- 页面头部 -->
    <div class="todolist-header">
      <h1 class="todolist-title">
        <i class="icon">📋</i>
        智能任务管理系统
      </h1>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-label">总任务</span>
          <span class="stat-value">{{ activeTasksCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">已完成</span>
          <span class="stat-value">{{ completedTasksCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">完成率</span>
          <span class="stat-value">{{ completionRate }}%</span>
        </div>
        <div class="stat-item worth-stat" @click="showWorthHistory">
          <span class="stat-label">成长基金</span>
          <span class="stat-value">¥{{ store.userWorth.toFixed(2) }}</span>
        </div>
        <div class="stat-item ranking-stat" @click="goToRanking">
          <span class="stat-label">查看排行</span>
          <span class="stat-value">🏆</span>
        </div>
        <div class="stat-item timetracker-stat" @click="goToTimeTracker">
          <span class="stat-label">时间记录</span>
          <span class="stat-value">⏰</span>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="todolist-main">
      <!-- 功能菜单区域 -->
      <div class="function-menu">
        <TodoToolbar />
      </div>

      <!-- 任务列表区域 -->
      <div class="task-list-area">
        <TaskList />
      </div>
    </div>

    <!-- 任务添加/编辑模态框 -->
    <TaskModal v-if="store.showTaskModal" :task="store.editingTask" @close="store.closeTaskModal" />

    <!-- 设置模态框 -->
    <SettingsModal v-if="store.showSettingsModal" @close="store.closeSettingsModal" />

    <!-- 价值历史记录模态框 -->
    <WorthHistoryModal v-if="store.showWorthHistoryModal" />

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useTodoStore } from './stores/todoStore.js'
import { initAutoUpdate, startAutoUpdate, stopAutoUpdate } from './utils/autoUpdateService.js'
import TodoToolbar from './components/TodoToolbar.vue'
import TaskList from './components/TaskList.vue'
import TaskModal from './components/TaskModal.vue'
import SettingsModal from './components/SettingsModal.vue'
import WorthHistoryModal from './components/WorthHistoryModal.vue'
import TopBar from '@/components/TopBar.vue'
import TodoService from '@/services/todoService.js'
import AuthService from '@/services/authService.js'
// import UserService from '@/services/userService.js'
import config from '@/config'
import UserMainService from '@/services/userMainService.js'

export default {
  name: 'TodoList',
  components: {
    TodoToolbar,
    TaskList,
    TaskModal,
    SettingsModal,
    WorthHistoryModal,
    TopBar
  },
  setup() {
    const store = useTodoStore()
    const loading = ref(true)

    // 用户信息和应用信息
    const user = ref({
      name: '用户名',
      id: 1,
      userType: 1,
      avatar: require('@/assets/logo.png')
    })

    const apps = ref([])

    // 计算属性
    const completedTasksCount = computed(() => {
      return store.completedActiveTasksCount
    })

    const completionRate = computed(() => {
      return store.activeTasksCompletionRate
    })

    const activeTasksCount = computed(() => {
      return store.activeTasks.length
    })

    // 加载用户信息
    const loadUserInfo = () => {
      // 从本地存储获取当前用户信息
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        console.log('当前用户信息:', currentUser);
        user.value.name = currentUser.nickname || currentUser.username || '用户名';
        user.value.id = currentUser.userid || currentUser.id || 1;
        user.value.userType = currentUser.userType || 1;
        console.log("icon:", currentUser.icon);
        // 如果有头像信息，则更新头像路径
        if (currentUser.icon) {
          // 规范化路径分隔符，将反斜杠转换为正斜杠
          const normalizedIcon = currentUser.icon.replace(/\\/g, '/');
          console.log('规范化后的icon路径:', normalizedIcon);

          // 构建完整的后端服务器地址，确保头像从8000端口获取
          if (normalizedIcon.startsWith('http')) {
            console.log('完整URL:', normalizedIcon);
            // 如果已经是完整URL，则直接使用
            user.value.avatar = normalizedIcon;
          } else if (normalizedIcon.startsWith('/extra-files/uploads/')) {
            console.log('extra-files URL:', normalizedIcon);
            // 从后端服务器的8000端口获取头像
            user.value.avatar = `${config.API_BASE_URL}${normalizedIcon}`;
          } else if (normalizedIcon.startsWith('uploads/')) {
            console.log('uploads URL:', normalizedIcon);
            // 从后端服务器的8000端口获取头像
            //user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;

            user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
            //查看地址：`${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
            console.log('user.value.avatar', user.value.avatar);
          } else {
            console.log('img URL:', normalizedIcon);
            user.value.avatar = `${config.API_BASE_URL}/extra-files/uploads/${normalizedIcon}`;
          }
        } else {
          console.log('使用默认头像');
          // 使用默认头像
          user.value.avatar = require('@/assets/logo.png');
        }
      }
    }

    // 加载应用信息
    const loadAppInfo = async () => {
      try {
        // 获取用户已订阅的应用
        const response = await UserMainService.getAllApps(user.value.userType);
        console.log('获取应用结果:', response);
        if (response.status === 200 && response.apps) {
          console.log('获取应用数据成功:', response.apps);
          apps.value = response.apps.map(app => ({
            id: app.id,
            name: app.name,
            desc: app.desc,
            link: app.name === '任务清单' ? '/todo' : app.link,
            icon: app.icon || 'code', // 根据应用名称设置图标
            used: app.used || false // 如果API返回了used字段则使用，否则默认为false
          }));
        }
      } catch (error) {
        console.error('获取应用数据失败:', error);
        // 如果获取失败，使用默认应用列表
        apps.value = [
          { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
          { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
          { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
          { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/Graph/', icon: 'share', used: false },
          { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/QuickResponse/', icon: 'quiz', used: false },
          { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
          { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
        ];
      }
    }

    // 显示价值历史记录
    const showWorthHistory = async () => {
      try {
        // 加载价值历史记录
        const worthHistoryResponse = await TodoService.getUserWorthHistory();
        if (worthHistoryResponse.status === 'ok') {
          store.updateWorthHistory(worthHistoryResponse.worth_history);
          store.openWorthHistoryModal();
        } else {
          console.error('获取价值历史记录失败:', worthHistoryResponse.message);
        }
      } catch (error) {
        console.error('获取价值历史记录异常:', error);
      }
    }

    // 跳转到排行榜页面
    const goToRanking = () => {
      // 跳转到排行榜页面
      window.location.href = '/ranking/';
    }

    // 跳转到时间跟踪器页面
    const goToTimeTracker = () => {
      // 跳转到时间跟踪器页面
      window.location.href = '/timetracker/';
    }

    // 从后端加载数据
    const loadDataFromBackend = async () => {
      try {
        loading.value = true;
        console.log('开始从后端加载数据...');

        // 获取所有todo数据
        const response = await TodoService.getAllTodoData();
        console.log('获取到的后端数据:', response);

        if (response.status === 'ok') {
          // 更新任务类型
          store.updateTypes(response.todo_types || []);

          // 更新任务周期
          store.updateCycles(response.todo_cycles || []);

          // 更新任务列表
          const tasks = (response.todo_list || []).map(item => ({
            id: item.id,
            title: item.title,
            description: item.category,
            completed: item.is_completed,
            typeId: item.typeid,
            cycleId: item.cycleid,
            beginTime: item.begin_time,
            endTime: item.end_time,
            difDate: item.dif_date,
            dayTime: item.day_time,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            // 新增字段
            current_count: item.current_count || 0,
            target_count: item.target_count || 1,
            worth: item.worth || 0,
            bind_event: item.bind_event || '',
            is_active: item.is_active !== undefined ? item.is_active : true
          }));
          store.updateTasks(tasks);

          console.log('数据加载完成，任务数量:', tasks.length);
        } else {
          console.error('获取数据失败:', response.message);
        }

        // 加载用户价值信息
        try {
          const worthResponse = await TodoService.getUserWorth();
          if (worthResponse.status === 'ok') {
            store.updateUserWorth(worthResponse.current_worth);
          } else {
            console.error('获取用户价值失败:', worthResponse.message);
          }
        } catch (error) {
          console.error('获取用户价值异常:', error);
        }
      } catch (error) {
        console.error('从后端加载数据时发生错误:', error);
      } finally {
        loading.value = false;
      }
    }

    // 显示通知
    const showNotification = (message, type = 'info') => {
      // 这里可以实现通知显示逻辑
      console.log(`[${type}] ${message}`);
    }

    // 初始化应用
    const initApp = async () => {
      try {
        console.log('TodoList系统初始化中...')

        // 加载用户信息和应用信息
        loadUserInfo()
        await loadAppInfo()

        // 从后端加载数据
        await loadDataFromBackend()

        // 初始化并启动自动更新服务
        initAutoUpdate(store, showNotification)
        startAutoUpdate()

        console.log('TodoList系统初始化完成')
      } catch (error) {
        console.error('TodoList系统初始化失败:', error)
      }
    }

    // 组件挂载时初始化
    onMounted(() => {
      initApp()
    })

    // 组件卸载时清理
    onUnmounted(() => {
      stopAutoUpdate()
    })

    return {
      store,
      loading,
      user,
      apps,
      completedTasksCount,
      completionRate,
      activeTasksCount,
      showWorthHistory,
      goToRanking,
      goToTimeTracker
    }
  }
}
</script>

<style scoped>
.todolist-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.todolist-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.todolist-title {
  margin: 0 0 20px 0;
  font-size: 2rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.todolist-title .icon {
  font-size: 1.5em;
}

.header-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.stat-item {
  background: rgba(255, 255, 255, 0.2);
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  min-width: 120px;
  backdrop-filter: blur(10px);
  transition: transform 0.2s, background 0.2s;
}

.stat-item:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.worth-stat {
  cursor: pointer;
}

.worth-stat:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.ranking-stat {
  cursor: pointer;
}

.ranking-stat:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.timetracker-stat {
  cursor: pointer;
}

.timetracker-stat:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.stat-label {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 5px;
  opacity: 0.9;
}

.stat-value {
  display: block;
  font-size: 1.4rem;
  font-weight: bold;
}

.todolist-main {
  display: flex;
  gap: 20px;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.function-menu {
  flex: 0 0 250px;
}

.task-list-area {
  flex: 1;
  min-width: 0;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  background: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .todolist-main {
    flex-direction: column;
  }

  .function-menu {
    flex: none;
  }

  .header-stats {
    gap: 10px;
  }

  .stat-item {
    min-width: 100px;
    padding: 10px;
  }

  .stat-label {
    font-size: 0.8rem;
  }

  .stat-value {
    font-size: 1.2rem;
  }
}
</style>